import React, { useState } from 'react';
import { FlexRow, BodyText, BoldTinyBodyText, SmallBodyText, NavigationItem, INavigationItemProps } from 'ui-toolkit';
import { NameControl } from './NameControl';
import { formatPhoneNumber } from 'utilities/shared/formatPhoneNumber';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import { IPartyEntity } from 'ui-toolkit/orders/interfaces/IPartyEntity';
import { FullNameOfPersonData } from '../../../../../ui-toolkit/orders/interfaces/IParty';
import { IHasTestData } from '../../../../../ui-toolkit/test-utilities/IHasTestData';
import { mergeTests } from '../../../../../ui-toolkit/test-utilities/mergeTests';

export interface IPartyEntityRowProps extends IHasTestData {
  entity: IPartyEntity;
  isUser: boolean;
  navEditStart?: INavigationItemProps;
}

const EntityLabel = styled(BoldTinyBodyText)`
  margin-top: ${({ theme }) => theme.spacing.spacing2};
  margin-bottom: ${({ theme }) => theme.spacing.spacing2};
`;

export const PartyEntityRow: React.FC<IPartyEntityRowProps> = ({
  entity,
  isUser,
  'data-test-id': dataTestID,
  navEditStart,
}) => {
  const [showMore, setShowMore] = useState(false);
  const hasMore = entity.signatories.length > 1;
  return (
    <>
      {entity.signatories.length === 0 ? (
        <FlexRow justifyContent="space-between">
          <NameControl
            data-test-id={mergeTests(dataTestID, 'name')}
            party={entity}
            showPartyType={true}
            intent="faded"
          />
          <NavigationItem text={`Edit`} {...navEditStart}>
            <FontAwesomeIcon icon={faEdit} />
          </NavigationItem>
        </FlexRow>
      ) : (
        <>
          {entity.signatories.map((sig, index) => {
            const personEmail = sig.email;
            const personPhone = formatPhoneNumber(sig.phoneNumber || '');

            if (showMore || index === 0) {
              return (
                <React.Fragment key={sig.id}>
                  <FlexRow justifyContent="space-between">
                    {index === 0 ? (
                      <>
                        <NameControl
                          data-test-id={mergeTests(dataTestID, 'name')}
                          party={entity}
                          intent="faded"
                          showPartyType={true}
                        />
                        <NavigationItem text={`Edit`} {...navEditStart}>
                          <FontAwesomeIcon icon={faEdit} />
                        </NavigationItem>
                      </>
                    ) : (
                      <div></div>
                    )}
                  </FlexRow>
                  <FlexRow>
                    <BodyText key={sig.id} data-test-id={mergeTests(dataTestID, `${index}`, 'name')}>
                      <EntityLabel>Signatory</EntityLabel>
                      <SmallBodyText intent="faded">{FullNameOfPersonData(sig)}</SmallBodyText>
                    </BodyText>
                    {showMore && index === entity.signatories.length - 1 ? (
                      <SmallBodyText intent="faded">
                        <NavigationItem isInline onClick={() => setShowMore(!showMore)} text="Show Less" />
                      </SmallBodyText>
                    ) : (
                      <></>
                    )}
                    {hasMore && !showMore && index === 0 ? (
                      <SmallBodyText intent="faded">
                        <NavigationItem
                          data-test-id={mergeTests(dataTestID, 'show-more')}
                          isInline
                          onClick={() => setShowMore(!showMore)}
                          text="Show More"
                        />
                      </SmallBodyText>
                    ) : (
                      <></>
                    )}
                  </FlexRow>
                  <>
                    <SmallBodyText intent="faded" data-test-id={mergeTests(dataTestID, 'email')}>
                      {personEmail || (personPhone ? '' : '-')}
                    </SmallBodyText>
                    <SmallBodyText intent="faded" data-test-id={mergeTests(dataTestID, 'phone')}>
                      {personPhone}
                    </SmallBodyText>
                  </>
                </React.Fragment>
              );
            }
            return <React.Fragment key={sig.id}></React.Fragment>;
          })}
        </>
      )}
    </>
  );
};
